// Formatting and roster helpers of the FIBA statistics sheet. Mirrors the stats app
// (msm-stats statisticsCalculations.ts) so both sheets print the same text.

const MAX_ROSTER_ROWS = 12

const pad2 = (n) => String(n).padStart(2, '0')

/** The boxscore's minutes come as HH:MM:SS; the sheet prints MM:SS. */
export const formatMinutes = (hhmmss) => (hhmmss ? String(hhmmss).slice(3, 8) : '00:00')

/** `MM:SS`, always two digits each. */
export const formatSeconds = (totalSeconds) => {
  const whole = Math.max(0, Math.round(totalSeconds || 0))
  return `${pad2(Math.floor(whole / 60))}:${pad2(whole % 60)}`
}

/** `made/attempted`, no spaces. */
export const formatMadeAttempted = (made, attempted) => `${made ?? 0}/${attempted ?? 0}`

/** One decimal, half-up, no % sign; `0/0` prints `0.0`. */
export const formatPercentage = (made, attempted) => {
  if (!attempted) return '0.0'
  return (Math.round((made / attempted) * 1000) / 10).toFixed(1)
}

/** Always signed, including `+0`. */
export const formatPlusMinus = (value) => (value >= 0 ? `+${value}` : `${value}`)

/** `11-0 (29-25)`: the run, then the game score (home first) when it ended. */
export const formatScoringRun = (run) => (run ? `${run.points}-0 (${run.home}-${run.away})` : '-')

/** `(18-23, 21-11, -, -)` with overtimes appended; a period without stats prints `-`. */
export const formatQuarterResults = (homeScores, awayScores, hasStats) => {
  const periods = Math.max(4, homeScores?.length || 0, awayScores?.length || 0)
  const cells = Array.from({ length: periods }, (_, i) =>
    hasStats?.[i] ? `${homeScores[i] ?? 0}-${awayScores[i] ?? 0}` : '-',
  )
  return `(${cells.join(', ')})`
}

/** `DD.MM.YYYY HH:MM` in Athens time, per the FIBA sheet. */
export const formatSheetDateTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const part = (options) => d.toLocaleString('en-GB', { timeZone: 'Europe/Athens', ...options })
  const date = part({ day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.')
  const time = part({ hour: '2-digit', minute: '2-digit', hour12: false })
  return `${date} ${time}`
}

/** Short uppercase team code for the grey header cells, e.g. "PAN". */
export const teamCode = (name) =>
  (name ?? '')
    // Keep Latin (incl. extended), Greek and Cyrillic letters only.
    .replace(/[^A-Za-zÀ-ɏͰ-ϿЀ-ӿ]/g, '')
    .slice(0, 3)
    .toUpperCase()

/**
 * Roster in ascending jersey order, capped to the 12 rows on the sheet. When a roster is
 * larger than 12, players who played are kept first and DNP players fill whatever rows remain,
 * so nobody with stats is dropped from the sheet.
 */
export const sortRoster = (players = [], hasPlayed = () => true) => {
  const byJersey = [...players].sort((a, b) => (a.jersey ?? 0) - (b.jersey ?? 0))
  if (byJersey.length <= MAX_ROSTER_ROWS) return byJersey
  const played = byJersey.filter(hasPlayed)
  const bench = byJersey.filter((p) => !hasPlayed(p))
  return [...played, ...bench]
    .slice(0, MAX_ROSTER_ROWS)
    .sort((a, b) => (a.jersey ?? 0) - (b.jersey ?? 0))
}
